import { VOICE_MAX_DURATION_MS, VOICE_UPLOAD_MAX_BYTES, VOICE_UPLOAD_TYPES } from "@scrile/contracts";

/**
 * Запись голосового в браузере через MediaRecorder.
 *
 * Форматы: Chrome и Android — webm/opus, Firefox — ogg/opus (или webm), Safari —
 * mp4/AAC (MediaRecorder есть с iOS 14.3, в настройках по умолчанию — с iOS 14.5;
 * webm Safari не пишет, а isTypeSupported("audio/webm") у него false). Поэтому формат
 * выбирается по isTypeSupported из списка, а не угадывается по браузеру; если не
 * подошёл ни один — пишем в формате по умолчанию и берём тип из самой записи.
 *
 * Микрофон — только в защищённом контексте (https, localhost): на http по адресу в
 * локальной сети getUserMedia нет, и кнопки записи не должно быть вовсе.
 *
 * Потолки контракта: 60 с (запись останавливается сама) и 2 МБ (битрейт 32 кбит/с даёт
 * ~240 КБ за минуту — запас на Safari, который битрейт не всегда соблюдает).
 */

export const RECORDER_MIME_PREFERENCE = [
  "audio/webm;codecs=opus",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/ogg;codecs=opus",
  "audio/webm",
  "audio/aac",
] as const;

export const RECORDER_BITS_PER_SECOND = 32_000;
/** Короче этого — случайное нажатие, а не сообщение. */
export const MIN_VOICE_MS = 700;

export interface RecorderEnv {
  isSecureContext: boolean;
  hasGetUserMedia: boolean;
  MediaRecorder: { isTypeSupported?(mime: string): boolean } | null;
}

export function recorderEnv(): RecorderEnv {
  if (typeof window === "undefined") return { isSecureContext: false, hasGetUserMedia: false, MediaRecorder: null };
  const recorder = (window as unknown as { MediaRecorder?: RecorderEnv["MediaRecorder"] }).MediaRecorder ?? null;
  return {
    isSecureContext: window.isSecureContext === true,
    hasGetUserMedia: typeof navigator !== "undefined" && typeof navigator.mediaDevices?.getUserMedia === "function",
    MediaRecorder: recorder,
  };
}

/** Можно ли вообще показать кнопку записи. */
export function canRecord(env: RecorderEnv): boolean {
  return env.isSecureContext && env.hasGetUserMedia && env.MediaRecorder !== null;
}

/**
 * Тип записи по возможностям браузера. "" — пусть браузер выберет сам (старый Safari без
 * isTypeSupported): тип потом берётся из blob.type.
 */
export function pickRecorderMime(isTypeSupported: ((mime: string) => boolean) | undefined): string {
  if (!isTypeSupported) return "";
  for (const mime of RECORDER_MIME_PREFERENCE) {
    try {
      if (isTypeSupported(mime)) return mime;
    } catch {
      // Safari бросал на неизвестной строке — просто следующий
    }
  }
  return "";
}

/** Тип без параметров: "audio/webm;codecs=opus" → "audio/webm". */
export function baseMime(mime: string): string {
  return mime.split(";")[0]?.trim().toLowerCase() ?? "";
}

/** Можно ли отправлять такой тип: из списка контракта (параметры кодека не мешают). */
export function isUploadableType(mime: string): boolean {
  return (VOICE_UPLOAD_TYPES as readonly string[]).includes(baseMime(mime));
}

/** Расширение файла по типу — серверу проще, и в скачанном файле видно, что это. */
export function extensionFor(mime: string): string {
  switch (baseMime(mime)) {
    case "audio/webm":
      return "webm";
    case "audio/ogg":
      return "ogg";
    case "audio/mp4":
    case "audio/x-m4a":
      return "m4a";
    case "audio/aac":
      return "aac";
    case "audio/mpeg":
      return "mp3";
    case "audio/wav":
      return "wav";
    default:
      return "bin";
  }
}

export type VoiceProblem = "too_short" | "too_long" | "too_big" | "bad_type" | null;

/** Проверка записи до загрузки — тем же, чем проверит сервер. */
export function checkVoice(input: { size: number; durationMs: number; mime: string }): VoiceProblem {
  if (input.durationMs < MIN_VOICE_MS || input.size === 0) return "too_short";
  if (!Number.isFinite(input.durationMs) || input.durationMs > VOICE_MAX_DURATION_MS) return "too_long";
  if (input.size > VOICE_UPLOAD_MAX_BYTES) return "too_big";
  if (!isUploadableType(input.mime)) return "bad_type";
  return null;
}

export interface VoiceRecording {
  blob: Blob;
  mime: string;
  durationMs: number;
}

export type RecorderError = "denied" | "unsupported" | "failed";

/**
 * Одна запись: start() просит микрофон и пишет, stop() отдаёт запись, cancel() —
 * выбрасывает. Дорожки микрофона закрываются в любом случае: иначе в iOS остаётся
 * красная полоса «микрофон включён».
 */
export class VoiceRecorder {
  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private startedAt = 0;
  private stoppedAt: number | null = null;
  private limitTimer: ReturnType<typeof setTimeout> | null = null;
  private finish: ((recording: VoiceRecording | null) => void) | null = null;
  private cancelled = false;
  private stopping: Promise<VoiceRecording | null> | null = null;

  constructor(
    private readonly options: {
      now?: () => number;
      /** Сработал потолок 60 с — запись остановлена сама. */
      onLimit?: () => void;
      maxMs?: number;
    } = {},
  ) {}

  get startTime(): number {
    return this.startedAt;
  }

  async start(): Promise<void> {
    const env = recorderEnv();
    if (!canRecord(env)) throw recorderError("unsupported");
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    } catch (error) {
      const name = error instanceof DOMException ? error.name : "";
      throw recorderError(name === "NotAllowedError" || name === "SecurityError" ? "denied" : "failed");
    }
    this.stream = stream;
    // Диалог разрешения микрофона может закрыться уже после ухода со страницы.
    if (this.cancelled) {
      this.release();
      return;
    }
    const mime = pickRecorderMime(env.MediaRecorder?.isTypeSupported?.bind(env.MediaRecorder));
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, {
        ...(mime ? { mimeType: mime } : {}),
        audioBitsPerSecond: RECORDER_BITS_PER_SECOND,
      });
    } catch {
      this.release();
      throw recorderError("unsupported");
    }
    this.recorder = recorder;
    this.chunks = [];
    recorder.addEventListener("dataavailable", (event) => {
      if (event.data && event.data.size > 0) this.chunks.push(event.data);
    });
    recorder.addEventListener("stop", () => {
      const durationMs = (this.stoppedAt ?? this.now()) - this.startedAt;
      const type = recorder.mimeType || mime || this.chunks[0]?.type || "audio/mp4";
      const blob = new Blob(this.chunks, { type });
      this.release();
      const done = this.finish;
      this.finish = null;
      done?.(this.cancelled ? null : { blob, mime: type, durationMs });
    });
    recorder.addEventListener("error", () => {
      this.cancelled = true;
      this.release();
      this.finish?.(null);
      this.finish = null;
    });
    // Кусками по секунде: Safari иначе отдаёт данные только в конце, и сбой стоит всей записи
    try {
      recorder.start(1_000);
    } catch {
      this.release();
      throw recorderError("failed");
    }
    this.startedAt = this.now();
    this.limitTimer = setTimeout(() => {
      this.options.onLimit?.();
      // Обёртка может не предоставить onLimit: сам рекордер всё равно обязан остановиться.
      void this.stop();
    }, Math.max(0, (this.options.maxMs ?? VOICE_MAX_DURATION_MS) - 200));
  }

  /** Остановить и получить запись; null — отменена или не шла. */
  stop(): Promise<VoiceRecording | null> {
    if (this.stopping) return this.stopping;
    this.stopping = new Promise((resolve) => {
      const recorder = this.recorder;
      if (!recorder || recorder.state === "inactive") {
        this.release();
        resolve(null);
        return;
      }
      this.finish = resolve;
      // stop-событие ждёт кодировщик; его задержка уже не является записью микрофона.
      this.stoppedAt = this.now();
      try {
        recorder.stop();
      } catch {
        this.release();
        this.finish = null;
        resolve(null);
      }
    });
    return this.stopping;
  }

  cancel(): void {
    this.cancelled = true;
    void this.stop();
  }

  private now(): number {
    return (this.options.now ?? (() => performance.now()))();
  }

  private release(): void {
    if (this.limitTimer) clearTimeout(this.limitTimer);
    this.limitTimer = null;
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    this.recorder = null;
  }
}

function recorderError(code: RecorderError): Error & { code: RecorderError } {
  return Object.assign(new Error(code), { code });
}

export function recorderErrorCode(error: unknown): RecorderError {
  if (typeof error === "object" && error !== null && "code" in error) {
    const code = (error as { code: unknown }).code;
    if (code === "denied" || code === "unsupported" || code === "failed") return code;
  }
  return "failed";
}
